/**
 * Custom Wallpaper Form - Add to Cart functionality
 *
 * Pricing: Taken from "Custom Size" variant price in Shopify admin
 * Price per sq ft = Custom Size variant price
 *
 * Formulas:
 * - Inches: sq_ft = (W × H) / 144
 * - Feet: sq_ft = W × H
 * - CM: sq_ft = (W / 30.48) × (H / 30.48)
 * - Price = sq_ft × variant price
 *
 * Product Types:
 * - Mural products: Show Custom Size tab only
 * - Pattern products: Show Roll Size tab (with predefined sizes) + Custom Size tab
 */

class CustomWallpaperForm {
  constructor(container) {
    this.container = container;
    this.productId = container.dataset.productId;
    this.isPatternProduct = container.dataset.isPattern === 'true';
    this.variants = JSON.parse(container.dataset.variants || '[]');
    this.sampleVariants = JSON.parse(container.dataset.sampleVariants || '[]');
    this.currency = container.dataset.currency || 'USD';
    this.moneyFormat = container.dataset.moneyFormat || '${{amount}}';

    // Price per sq ft from Custom Size variant (set in Shopify admin)
    this.pricePerSqFt = parseFloat(container.dataset.pricePerSqft) || 4.65;

    // No minimum order

    // Find the "Custom Size" variant for each material
    this.customSizeVariants = this.findCustomSizeVariants();

    this.init();
  }

  /**
   * Find variants that have "Custom Size" in their title/options
   * Returns an object mapping material names to variant IDs
   */
  findCustomSizeVariants() {
    const customVariants = {};

    this.variants.forEach(variant => {
      const title = (variant.title || '').toLowerCase();
      const option1 = (variant.option1 || '').toLowerCase();
      const option2 = (variant.option2 || '').toLowerCase();

      // Check if this is a "Custom Size" variant
      if (title.includes('custom size') || option2.includes('custom size')) {
        // Map by material name
        const material = variant.option1 || variant.title.split('/')[0].trim();
        const materialKey = material.toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');

        customVariants[materialKey] = variant;

        // Also map common variations
        if (materialKey.includes('peel') || materialKey.includes('stick')) {
          customVariants['peel-and-stick'] = variant;
          customVariants['peel-stick-removable'] = variant;
        }
        if (materialKey.includes('non-woven') || materialKey.includes('nonwoven')) {
          customVariants['non-woven-traditional'] = variant;
          customVariants['non-woven-paper'] = variant;
        }
        if (materialKey.includes('pre-pasted') || materialKey.includes('prepasted') || materialKey.includes('water')) {
          customVariants['pre-pasted'] = variant;
          customVariants['pre-pasted-water'] = variant;
        }
      }
    });

    return customVariants;
  }

  /**
   * Get the Custom Size variant for a specific material
   */
  getCustomSizeVariant(material) {
    const materialKey = material.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    return this.customSizeVariants[materialKey] ||
           this.customSizeVariants['peel-and-stick'] || // fallback
           Object.values(this.customSizeVariants)[0]; // any custom size variant
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.updateUnitDisplay();
    this.selectDefaultMaterial();

    if (this.isPatternProduct) {
      this.initTabs();
      this.calculateRollPrice(); // Initialize roll price display
    }
  }

  cacheElements() {
    // Tabs
    this.tabs = this.container.querySelectorAll('.wallpaper-tab');
    this.tabContents = this.container.querySelectorAll('.wallpaper-tab-content');

    // Custom Size elements
    this.unitRadios = this.container.querySelectorAll('input[name="wallpaper-unit"]');
    this.widthInput = this.container.querySelector('#wallpaper-width');
    this.heightInput = this.container.querySelector('#wallpaper-height');
    this.unitAbbrs = this.container.querySelectorAll('.wallpaper-unit-abbr');
    this.calcDisplay = this.container.querySelector('.wallpaper-calc-display');
    this.calcDimensions = this.container.querySelector('.wallpaper-calc-dimensions');
    this.calcPrice = this.container.querySelector('.wallpaper-calc-price');

    // Dynamic checkout forms
    this.dynamicCheckoutVariantInputs = this.container.querySelectorAll('[data-dynamic-checkout-variant]');

    // Roll Size elements
    this.rollSizeSelect = this.container.querySelector('#wallpaper-roll-size');
    this.rollQuantityInput = this.container.querySelector('#wallpaper-roll-quantity');
    this.rollUnitRadios = this.container.querySelectorAll('input[name="wallpaper-roll-unit"]');
    this.rollPriceDisplay = this.container.querySelector('.wallpaper-roll-price');

    // Material selection (Custom Size tab)
    this.paperOptions = this.container.querySelectorAll('.wallpaper-paper-option');
    this.paperRadios = this.container.querySelectorAll('input[name="wallpaper-paper-type"]');

    // Material selection (Roll Size tab)
    this.rollMaterialRadios = this.container.querySelectorAll('input[name="wallpaper-roll-material"]');
    this.rollMaterialOptions = this.container.querySelectorAll('[data-tab-content="roll-size"] .wallpaper-paper-option');

    // Notes/Customization inputs
    this.notesInput = this.container.querySelector('#wallpaper-notes');
    this.customizationInput = this.container.querySelector('#wallpaper-customization');
    this.rollNotesInput = this.container.querySelector('#wallpaper-roll-notes');
    this.rollCustomizationInput = this.container.querySelector('#wallpaper-roll-customization');

    // Buttons
    this.addToCartBtns = this.container.querySelectorAll('.wallpaper-add-to-cart');
    this.buySampleBtn = this.container.querySelector('.wallpaper-buy-sample');
    this.wishlistBtn = this.container.querySelector('.wallpaper-wishlist');

    // Sample Modal
    this.sampleModal = this.container.querySelector('.wallpaper-sample-modal');
    this.sampleModalClose = this.container.querySelector('.wallpaper-sample-modal-close');
    this.sampleItems = this.container.querySelectorAll('.wallpaper-sample-item');
    this.sampleAddToCart = this.container.querySelector('.wallpaper-sample-add-to-cart');
  }

  bindEvents() {
    // Unit selection
    this.unitRadios.forEach(radio => {
      radio.addEventListener('change', () => this.updateUnitDisplay());
    });

    // Dimension inputs
    if (this.widthInput) {
      this.widthInput.addEventListener('input', () => this.calculatePrice());
    }
    if (this.heightInput) {
      this.heightInput.addEventListener('input', () => this.calculatePrice());
    }

    // Roll size events
    if (this.rollSizeSelect) {
      this.rollSizeSelect.addEventListener('change', () => this.calculateRollPrice());
    }
    if (this.rollQuantityInput) {
      this.rollQuantityInput.addEventListener('input', () => this.calculateRollPrice());
    }
    this.rollUnitRadios?.forEach(radio => {
      radio.addEventListener('change', () => this.calculateRollPrice());
    });

    // Roll material selection
    this.rollMaterialOptions?.forEach(option => {
      option.addEventListener('click', () => this.selectRollMaterial(option));
    });

    // Paper type selection (Custom Size tab)
    this.paperOptions.forEach(option => {
      option.addEventListener('click', () => this.selectPaperType(option));
    });

    // Add to cart buttons
    this.addToCartBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleAddToCart(e));
    });

    // Sample modal
    if (this.buySampleBtn) {
      this.buySampleBtn.addEventListener('click', () => this.openSampleModal());
    }
    if (this.sampleModalClose) {
      this.sampleModalClose.addEventListener('click', () => this.closeSampleModal());
    }
    if (this.sampleModal) {
      this.sampleModal.addEventListener('click', (e) => {
        if (e.target === this.sampleModal) this.closeSampleModal();
      });
    }

    // Sample item selection
    this.sampleItems.forEach(item => {
      item.addEventListener('click', () => this.toggleSampleItem(item));
    });

    // Sample add to cart
    if (this.sampleAddToCart) {
      this.sampleAddToCart.addEventListener('click', () => this.handleSampleAddToCart());
    }

    // Wishlist
    if (this.wishlistBtn) {
      this.wishlistBtn.addEventListener('click', () => this.handleWishlist());
    }

    // Notes toggle (collapsible)
    this.container.querySelectorAll('[data-notes-toggle]').forEach(toggle => {
      toggle.addEventListener('click', () => this.toggleNotes(toggle));
    });
  }

  toggleNotes(toggle) {
    const notesContainer = toggle.closest('.wallpaper-notes');
    if (notesContainer) {
      notesContainer.classList.toggle('open');
    }
  }

  initTabs() {
    this.tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = tab.dataset.tab;
        this.switchTab(tabName);
      });
    });
  }

  switchTab(tabName) {
    this.tabs.forEach(tab => {
      tab.classList.toggle('active', tab.dataset.tab === tabName);
    });
    this.tabContents.forEach(content => {
      content.classList.toggle('active', content.dataset.tabContent === tabName);
    });

    // Sync material selection between tabs
    this.syncMaterialSelection(tabName);
  }

  /**
   * Sync material selection when switching tabs
   */
  syncMaterialSelection(targetTab) {
    let sourceMaterial = '';

    if (targetTab === 'roll-size') {
      // Get selected material from Custom Size tab
      const customMaterialRadio = this.container.querySelector('input[name="wallpaper-paper-type"]:checked');
      if (customMaterialRadio) {
        sourceMaterial = customMaterialRadio.value;
      }

      // Find and select matching material in Roll Size tab
      if (sourceMaterial) {
        const rollMaterialRadio = this.container.querySelector(`input[name="wallpaper-roll-material"][value="${sourceMaterial}"]`);
        if (rollMaterialRadio) {
          rollMaterialRadio.checked = true;
          const option = rollMaterialRadio.closest('.wallpaper-paper-option');
          this.rollMaterialOptions?.forEach(opt => opt.classList.remove('selected'));
          if (option) option.classList.add('selected');
          this.calculateRollPrice();
        }
      }
    } else if (targetTab === 'custom-size') {
      // Get selected material from Roll Size tab
      const rollMaterialRadio = this.container.querySelector('input[name="wallpaper-roll-material"]:checked');
      if (rollMaterialRadio) {
        sourceMaterial = rollMaterialRadio.value;
      }

      // Find and select matching material in Custom Size tab
      if (sourceMaterial) {
        const customMaterialRadio = this.container.querySelector(`input[name="wallpaper-paper-type"][value="${sourceMaterial}"]`);
        if (customMaterialRadio) {
          customMaterialRadio.checked = true;
          const option = customMaterialRadio.closest('.wallpaper-paper-option');
          this.paperOptions?.forEach(opt => opt.classList.remove('selected'));
          if (option) option.classList.add('selected');
          this.calculatePrice();
        }
      }
    }
  }

  getSelectedUnit() {
    const checked = this.container.querySelector('input[name="wallpaper-unit"]:checked');
    return checked ? checked.value : 'ft';
  }

  updateUnitDisplay() {
    const unit = this.getSelectedUnit();
    const abbr = unit === 'cm' ? 'cm' : unit === 'ft' ? 'ft' : 'in';

    this.unitAbbrs.forEach(el => {
      el.textContent = abbr;
    });

    this.calculatePrice();
  }

  selectDefaultMaterial() {
    const firstOption = this.container.querySelector('.wallpaper-paper-option');
    if (firstOption) {
      this.selectPaperType(firstOption);
    }

    // Set initial dynamic checkout variant
    const selectedPaperInput = this.container.querySelector('input[name="wallpaper-paper-type"]:checked');
    if (selectedPaperInput?.dataset.variantId) {
      this.updateDynamicCheckoutVariant(selectedPaperInput.dataset.variantId);
    }
  }

  selectPaperType(option) {
    this.paperOptions.forEach(opt => opt.classList.remove('selected'));
    option.classList.add('selected');

    const radio = option.querySelector('input[type="radio"]');
    if (radio) {
      radio.checked = true;
      this.updateDynamicCheckoutVariant(radio.dataset.variantId);
    }

    this.calculatePrice();
  }

  selectRollMaterial(option) {
    this.rollMaterialOptions?.forEach(opt => opt.classList.remove('selected'));
    option.classList.add('selected');

    const radio = option.querySelector('input[type="radio"]');
    if (radio) {
      radio.checked = true;
    }

    this.calculateRollPrice();
  }

  getSelectedRollMaterial() {
    const checked = this.container.querySelector('input[name="wallpaper-roll-material"]:checked');
    return checked?.dataset?.material || '';
  }

  getSelectedRollSize() {
    if (!this.rollSizeSelect) return '';
    const selectedOption = this.rollSizeSelect.options[this.rollSizeSelect.selectedIndex];
    return selectedOption?.dataset?.size || '';
  }

  /**
   * Find variant by matching size and material options
   */
  findVariantBySizeAndMaterial(size, material) {
    if (!size || !material) return null;

    const sizeLower = size.toLowerCase();
    const materialLower = material.toLowerCase();

    return this.variants.find(variant => {
      const option1 = (variant.option1 || '').toLowerCase();
      const option2 = (variant.option2 || '').toLowerCase();

      // Check if option1 is material and option2 is size (or vice versa)
      const matchesMaterialFirst = option1.includes(materialLower.substring(0, 10)) && option2 === sizeLower;
      const matchesSizeFirst = option1 === sizeLower && option2.includes(materialLower.substring(0, 10));

      return matchesMaterialFirst || matchesSizeFirst;
    });
  }

  updateDynamicCheckoutVariant(variantId) {
    if (!variantId) return;

    this.dynamicCheckoutVariantInputs?.forEach(input => {
      input.value = variantId;
    });
  }

  getSelectedMaterial() {
    const checked = this.container.querySelector('input[name="wallpaper-paper-type"]:checked');
    return checked ? checked.value : 'peel-and-stick';
  }

  getSelectedMaterialName() {
    const checked = this.container.querySelector('input[name="wallpaper-paper-type"]:checked');
    if (checked) {
      const label = checked.closest('.wallpaper-paper-option')?.querySelector('.wallpaper-paper-name');
      return label?.textContent?.trim() || checked.value;
    }
    return 'Peel and Stick';
  }

  /**
   * Calculate square feet based on dimensions and unit
   *
   * Formulas:
   * - Inches: sq_ft = (W × H) / 144
   * - Feet: sq_ft = W × H
   * - CM: sq_ft = (W / 30.48) × (H / 30.48)
   */
  calculateSqFt(width, height, unit) {
    if (!width || !height || width <= 0 || height <= 0) {
      return 0;
    }

    let sqFt;

    switch (unit) {
      case 'in':
        // Inches to sq ft: divide by 144 (12 × 12)
        sqFt = (width * height) / 144;
        break;
      case 'ft':
        // Already in feet
        sqFt = width * height;
        break;
      case 'cm':
        // CM to feet: divide by 30.48
        sqFt = (width / 30.48) * (height / 30.48);
        break;
      default:
        sqFt = (width * height) / 144;
    }

    return sqFt;
  }

  /**
   * Calculate price based on square footage
   * Price = sq_ft × $4.65/sq ft
   * Display uses rounded sq ft to match cart total
   */
  calculatePrice() {
    const width = parseFloat(this.widthInput?.value) || 0;
    const height = parseFloat(this.heightInput?.value) || 0;
    const unit = this.getSelectedUnit();

    const sqFt = this.calculateSqFt(width, height, unit);
    const roundedSqFt = Math.ceil(sqFt); // Round up for cart quantity
    const price = roundedSqFt * this.pricePerSqFt; // Price based on rounded sq ft
    const priceInCents = Math.round(price * 100);

    // Update dimensions display
    if (this.calcDimensions) {
      if (width > 0 && height > 0) {
        const unitLabel = unit === 'ft' ? "'" : (unit === 'cm' ? 'cm' : '"');
        this.calcDimensions.textContent = `${width}${unitLabel} x ${height}${unitLabel} = ${roundedSqFt} sq. ft`;
      } else {
        this.calcDimensions.textContent = '';
      }
    }

    // Update price display (matches cart total exactly)
    if (this.calcPrice) {
      this.calcPrice.textContent = this.formatMoney(priceInCents);
    }

    return { sqFt, roundedSqFt, price, priceInCents };
  }

  calculateRollPrice() {
    if (!this.rollSizeSelect || !this.rollQuantityInput) return;

    const size = this.getSelectedRollSize();
    const material = this.getSelectedRollMaterial();
    const quantity = parseInt(this.rollQuantityInput.value) || 2;

    // Find the variant that matches both size and material
    const variant = this.findVariantBySizeAndMaterial(size, material);

    let price = 0;
    let variantId = null;

    if (variant) {
      price = variant.price;
      variantId = variant.id;
    }

    const totalPrice = price * quantity;

    if (this.rollPriceDisplay) {
      this.rollPriceDisplay.textContent = this.formatMoney(totalPrice);
    }

    // Update dynamic checkout form variant
    if (variantId) {
      this.updateDynamicCheckoutVariant(variantId);
    }

    return { price: totalPrice, variantId, variant };
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  async handleAddToCart(e) {
    e.preventDefault();

    // Determine which tab is active
    const activeTab = this.container.querySelector('.wallpaper-tab.active');
    const isRollSize = activeTab?.dataset.tab === 'roll-size';

    let cartData;

    if (isRollSize) {
      cartData = this.prepareRollSizeCartData();
    } else {
      cartData = this.prepareCustomSizeCartData();
    }

    if (!cartData) return;

    // Find the clicked button
    const btn = e.target.closest('.wallpaper-add-to-cart');
    this.setButtonLoading(btn, true);

    try {
      // Get cart drawer element
      const cartDrawer = document.querySelector('cart-drawer');

      // Build form data
      const formData = new FormData();
      formData.append('id', cartData.id);
      formData.append('quantity', cartData.quantity);

      // Add properties if present
      if (cartData.properties) {
        for (const [key, value] of Object.entries(cartData.properties)) {
          formData.append(`properties[${key}]`, value);
        }
      }

      // Add sections for cart drawer update
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        const sections = cartDrawer.getSectionsToRender().map(section => section.id);
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const result = await response.json();

      // Update and open cart drawer
      if (cartDrawer) {
        if (typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(result);
        }
        cartDrawer.classList.add('open');
        cartDrawer.classList.remove('is-empty');
        document.body.classList.add('no-scroll');
      } else {
        // Fallback: redirect to cart
        window.location = '/cart';
      }

    } catch (error) {
      console.error('Add to cart error:', error);
      this.showNotification('Error adding to cart. Please try again.', 'error');
    } finally {
      this.setButtonLoading(btn, false);
    }
  }

  /**
   * Prepare cart data for custom size orders
   *
   * Uses quantity = square feet (rounded up)
   * Uses "Custom Size" variant priced at $4.65
   * Total = $4.65 × sq_ft
   */
  prepareCustomSizeCartData() {
    const width = parseFloat(this.widthInput?.value) || 0;
    const height = parseFloat(this.heightInput?.value) || 0;

    if (width <= 0 || height <= 0) {
      this.showNotification('Please enter valid dimensions', 'error');
      return null;
    }

    const unit = this.getSelectedUnit();
    const material = this.getSelectedMaterial();
    const materialName = this.getSelectedMaterialName();
    const { sqFt, roundedSqFt, price, priceInCents } = this.calculatePrice();

    // Get notes from Custom Size tab - query directly to ensure we get current value
    const customSizeTab = this.container.querySelector('[data-tab-content="custom-size"]');
    const notesTextarea = customSizeTab?.querySelector('#wallpaper-notes') || this.notesInput;
    const notes = notesTextarea?.value || '';

    // Get the Custom Size variant for the selected material
    const customSizeVariant = this.getCustomSizeVariant(material);

    if (!customSizeVariant) {
      this.showNotification('Custom Size variant not found. Please contact support.', 'error');
      return null;
    }

    const unitLabel = unit === 'ft' ? 'ft' : (unit === 'cm' ? 'cm' : 'in');

    // Material is already part of the variant, so don't duplicate it
    const properties = {
      'Dimensions': `${width} x ${height} ${unitLabel}`,
      'Square Feet': roundedSqFt,
      '_quantity_locked': 'true' // Prevents quantity editing in cart
    };

    // Add notes if provided
    if (notes.trim()) {
      properties['Notes'] = notes.trim();
    }

    return {
      id: customSizeVariant.id,
      quantity: roundedSqFt, // Quantity = sq ft for accurate pricing with $4.65 variant
      properties
    };
  }

  /**
   * Prepare cart data for roll size orders (predefined sizes)
   */
  prepareRollSizeCartData() {
    if (!this.rollSizeSelect) return null;

    const size = this.getSelectedRollSize();
    const material = this.getSelectedRollMaterial();
    const quantity = parseInt(this.rollQuantityInput?.value) || 2;

    // Minimum quantity is 2 for roll size
    if (quantity < 2) {
      this.showNotification('Minimum quantity is 2 for roll size orders.', 'error');
      return null;
    }

    if (!size) {
      this.showNotification('Please select a size', 'error');
      return null;
    }

    if (!material) {
      this.showNotification('Please select a material', 'error');
      return null;
    }

    // Find the variant that matches both size and material
    const variant = this.findVariantBySizeAndMaterial(size, material);

    if (!variant) {
      this.showNotification('Selected combination not available. Please try different options.', 'error');
      return null;
    }

    // Get notes from Roll Size tab - query directly to ensure we get current value
    const rollSizeTab = this.container.querySelector('[data-tab-content="roll-size"]');
    const notesTextarea = rollSizeTab?.querySelector('#wallpaper-roll-notes') || this.rollNotesInput;
    const notes = notesTextarea?.value || '';

    // Size and Material are already part of the variant, so only add notes if provided
    const properties = {};

    if (notes.trim()) {
      properties['Notes'] = notes.trim();
    }

    return {
      id: variant.id,
      quantity: quantity,
      properties: Object.keys(properties).length > 0 ? properties : undefined
    };
  }

  openSampleModal() {
    if (this.sampleModal) {
      this.sampleModal.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  closeSampleModal() {
    if (this.sampleModal) {
      this.sampleModal.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  toggleSampleItem(item) {
    item.classList.toggle('selected');
    const checkbox = item.querySelector('input[type="checkbox"]');
    if (checkbox) {
      checkbox.checked = item.classList.contains('selected');
    }
  }

  async handleSampleAddToCart() {
    const selectedSamples = this.container.querySelectorAll('.wallpaper-sample-item.selected');

    if (selectedSamples.length === 0) {
      this.showNotification('Please select at least one sample', 'error');
      return;
    }

    this.setButtonLoading(this.sampleAddToCart, true);

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const items = [];

      selectedSamples.forEach(item => {
        const variantId = item.dataset.variantId;
        if (variantId) {
          items.push({
            id: parseInt(variantId),
            quantity: 1
          });
        }
      });

      // Build request body with sections for cart drawer update
      const requestBody = { items };

      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        const sections = cartDrawer.getSectionsToRender().map(section => section.id);
        requestBody.sections = sections.join(',');
        requestBody.sections_url = window.location.pathname;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to add samples to cart');
      }

      const result = await response.json();

      this.closeSampleModal();

      // Update and open cart drawer
      if (cartDrawer) {
        if (typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(result);
        }
        cartDrawer.classList.add('open');
        cartDrawer.classList.remove('is-empty');
        document.body.classList.add('no-scroll');
      } else {
        // Fallback: redirect to cart
        window.location = '/cart';
      }

    } catch (error) {
      console.error('Sample add to cart error:', error);
      this.showNotification('Error adding samples. Please try again.', 'error');
    } finally {
      this.setButtonLoading(this.sampleAddToCart, false);
    }
  }

  handleWishlist() {
    const event = new CustomEvent('wishlist:add', {
      detail: { productId: this.productId }
    });
    document.dispatchEvent(event);
    this.showNotification('Added to wishlist!');
  }

  setButtonLoading(button, loading) {
    if (!button) return;

    if (loading) {
      button.disabled = true;
      button.dataset.originalText = button.textContent;
      button.innerHTML = '<span class="wallpaper-btn-loading"><span class="wallpaper-spinner"></span> Adding...</span>';
    } else {
      button.disabled = false;
      button.textContent = button.dataset.originalText || 'Add to cart';
    }
  }

  showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `wallpaper-notification wallpaper-notification-${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 16px 24px;
      background: ${type === 'error' ? '#f44336' : '#4caf50'};
      color: white;
      border-radius: 8px;
      z-index: 10000;
      font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: slideIn 0.3s ease;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
}

// Add notification animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  @keyframes slideOut {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(100%); opacity: 0; }
  }
`;
document.head.appendChild(style);

// Initialize all wallpaper forms on page load
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.wallpaper-form-container').forEach(container => {
    new CustomWallpaperForm(container);
  });
});

// Also initialize for dynamically loaded content (Shopify theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('.wallpaper-form-container').forEach(container => {
      new CustomWallpaperForm(container);
    });
  });
}
